// Lib
import React from 'react';
import { connect, useSelector } from 'react-redux';

// Utils
import { getElementId } from '../../../common/elements/utils/elementPropertyUtils';

// Actions
import { navigateToBoard, navigateToLogin } from '../../reducers/navigationActions';

// Selectors
import { getElementChildrenIdsThunk } from '../../element/selectors/elementTraversalSelector';
import { selectIsSplashVisible } from '../../auth/modals/splash/AppSplash';

// Types
import { ImMNElement } from '../../../common/elements/elementModelTypes';

const ROOT_PATHNAME = '/';
const isRoot = (pathname: string) => pathname === ROOT_PATHNAME;

interface WorkspaceRootRedirectionManagerProps {
    currentBoard: ImMNElement;
    isUserAvailable: boolean;
    isGuest: boolean;
    location: Location;
    dispatchRedirectToLogin: (from: string) => void;
    dispatchRedirectToBoard: (boardId: string) => void;
    dispatchGetElementChildrenIds: (elementIds: string[]) => string[];
}

const mapDispatchToProps = (dispatch: Function) => ({
    dispatchRedirectToLogin: (from: string) => dispatch(navigateToLogin({ from })),
    dispatchRedirectToBoard: (boardId: string) => dispatch(navigateToBoard({ boardId, trackTiming: false })),
    dispatchGetElementChildrenIds: (elementIds: string[]) => dispatch(getElementChildrenIdsThunk(elementIds)),
});

/**
 * This component is being used to separate the redirection logic out of the WorkspaceInitContainer to account
 * for the scenario that the root URL is loaded (e.g. app.milanote.com) because sub routes are not invoked in
 * this version of React Router when the current route matches exactly ('/').
 */
const WorkspaceRootRedirectionManager = (props: WorkspaceRootRedirectionManagerProps): null => {
    const { location, isUserAvailable, isGuest, currentBoard, dispatchRedirectToLogin, dispatchRedirectToBoard } =
        props;
    const pathname = location?.pathname || '';

    // We need to get the current board ID from the current board because we want to ensure that the
    // current board is available before performing the redirection (so the slug, etc is available).
    const currentBoardId = getElementId(currentBoard);

    const isSplashEnabled = useSelector((state) => selectIsSplashVisible(state, location));

    React.useEffect(() => {
        if (!isRoot(pathname)) return;

        if (isGuest) {
            if (isSplashEnabled) return;

            dispatchRedirectToLogin(pathname);
            return;
        }

        if (isUserAvailable && currentBoardId) {
            dispatchRedirectToBoard(currentBoardId);
            return;
        }
    }, [pathname, isUserAvailable, currentBoardId, isSplashEnabled]);

    return null;
};

export default connect(null, mapDispatchToProps)(WorkspaceRootRedirectionManager);
